import React, { FC } from 'react';
import { Routes, Route, Outlet } from 'react-router-dom';
import { AuthGuard, EnigmaGuard } from '@/Domains/Auth/UI/Guards';
import {
	ChallengeForm,
	LogInForm,
	LogOutLayout,
} from '@/Domains';

import { ErrorPage, HomePage } from '@/Shared/UI/Pages';

import { USER_ROLES } from '@/Shared/Helpers';
import { ENTITY_ROUTES } from '@/Shared/Config/routing-config';


interface RoutingProps { }

/**
 * =====================================================
 * Routing
 * =====================================================
 *
 * Main routing component of the application.
 *
 * Responsibilities:
 * - Defines all app routes using React Router v6.
 * - Wraps routes with guards:
 *    - `AuthGuard` for protected routes requiring authentication.
 *    - `EnigmaGuard` for public routes with challenge validation.
 * - Supports nested routes for entities (contacts, needs, mandates, etc.) with role-based access.
 * - Handles dynamic route parameters for forms and details.
 * - Provides fallback error page for unmatched routes (404).
 *
 * Author: Samano CASTRE
 * Date: July 16, 2025
 */
export const Routing: FC<RoutingProps> = () => (
	<Routes>
		{/* =====================================================
		    Public routes
		    ===================================================== */}
		<Route index element={<ChallengeForm />} />,
		<Route path='logout' element={<AuthGuard requiredRole={USER_ROLES.USER}><LogOutLayout /></AuthGuard>} />,
		<Route path="home" element={<AuthGuard requiredRole={USER_ROLES.USER}><HomePage /></AuthGuard>} />,

		{/* =====================================================
		    Authenticated routes
		    ===================================================== */}
		<Route path="login" element={<EnigmaGuard><LogInForm /></EnigmaGuard>} />,


		{/* =====================================================
		    Dynamic entity routes
		    =====================================================
		    Loops over `ENTITY_ROUTES_NEW` configuration to define
		    nested entity routes with optional forms and detail pages.
		*/}
		{ENTITY_ROUTES.map(({
			path,
			role,
			Layout,
			Form,
			Detail,
			FormSelector
		}) => (

			<Route key={path} path={path} element={<AuthGuard requiredRole={role}><Outlet /></AuthGuard>}>
				<Route index element={<Layout />} />
				{Form && <>
					<Route path="new/:category?/:type?" element={<Form />} />
					<Route path=":id/edit" element={<Form />} />
				</>}
				{FormSelector
					&& <>
						<Route path="selector/:category?" element={<FormSelector />} />
						{Form && <Route path="selector/:category/:type" element={<Form />} />}
					</>
				}
				{Detail && <Route path=":id" element={<Detail />} />}
				<Route path="*" element={<ErrorPage />} />
			</Route>
		))}
		<Route path="error/:code" element={<ErrorPage />} />
		<Route path="*" element={<ErrorPage />} />

	</Routes>
);


/**
<Route path="new" element={<Form />} />
					{hasFormSubType && <>
						<Route path="new/:category/:type" element={<Form />} />
						<Route path="new/:category" element={<Form />} />
					</>
					}*/